<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsAPI from 'dashboard/api/contacts';
import AppointmentsAPI from 'dashboard/api/appointments';
import AppointmentReminderTemplatesAPI from 'dashboard/api/appointmentReminderTemplates';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  appointment: {
    type: Object,
    default: null,
  },
  initialDate: {
    type: Date,
    default: null,
  },
  prefilledContactId: {
    type: Number,
    default: null,
  },
  prefilledConversationId: {
    type: Number,
    default: null,
  },
  conversationInboxId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close', 'save', 'delete']);

const { t } = useI18n();
const { currentAccount } = useAccount();

const title = ref('');
const description = ref('');
const startDate = ref('');
const startTime = ref('');
const endDate = ref('');
const endTime = ref('');
const allDay = ref(false);
const location = ref('');
const color = ref('#3b82f6');
const contactId = ref(null);
const conversationId = ref(null);
const sendReminder = ref(true);
const reminderMinutes = ref(30);
const reminderType = ref('message');
const reminderMessage = ref('');
const reminderInboxId = ref(null);
const selectedTemplateId = ref(null);
const reminderTemplates = ref([]);

// Get inboxes list from store
const inboxesList = useMapGetter('inboxes/getInboxes');

// Default reminder template
const defaultReminderTemplate = `Olá [contact_name], lembrando do seu compromisso '[appointment_title]' em [appointment_date] às [appointment_time].`;

// Get account settings for appointment reminders
const accountSettings = computed(() => currentAccount.value?.settings || {});

// Filter inboxes that support messaging (WhatsApp, API, etc)
const availableInboxes = computed(() => {
  return inboxesList.value.filter(inbox => {
    // Include inboxes that can send outgoing messages
    return [
      'Channel::Api',
      'Channel::Whatsapp',
      'Channel::TwilioSms',
      'Channel::Sms',
    ].includes(inbox.channel_type);
  });
});

// Contact search state
const searchContact = ref('');
const showContactDropdown = ref(false);
const isSearching = ref(false);
const searchResults = ref([]);
const selectedContact = ref(null);
let searchTimeout = null;

const isEditing = computed(() => !!props.appointment);
const isSendingReminder = ref(false);

// Can send reminder if editing an appointment with a contact and inbox
const canSendReminder = computed(() => {
  if (!isEditing.value || !contactId.value) return false;
  // Check if template has inbox or manual inbox is set
  if (selectedTemplateId.value && selectedTemplate.value) {
    return selectedTemplate.value.inbox_id || reminderInboxId.value;
  }
  return reminderInboxId.value;
});

const colors = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
];

const reminderOptions = [
  { value: 5, label: t('APPOINTMENTS.REMINDER_OPTIONS.5_MINUTES') },
  { value: 15, label: t('APPOINTMENTS.REMINDER_OPTIONS.15_MINUTES') },
  { value: 30, label: t('APPOINTMENTS.REMINDER_OPTIONS.30_MINUTES') },
  { value: 60, label: t('APPOINTMENTS.REMINDER_OPTIONS.1_HOUR') },
  { value: 120, label: t('APPOINTMENTS.REMINDER_OPTIONS.2_HOURS') },
  { value: 1440, label: t('APPOINTMENTS.REMINDER_OPTIONS.1_DAY') },
  { value: 2880, label: t('APPOINTMENTS.REMINDER_OPTIONS.2_DAYS') },
];

// Fetch reminder templates
const fetchReminderTemplates = async () => {
  try {
    const response = await AppointmentReminderTemplatesAPI.getActive();
    reminderTemplates.value = response.data;
  } catch (error) {
    console.error('Error fetching reminder templates:', error);
  }
};

// Get selected template object
const selectedTemplate = computed(() => {
  if (!selectedTemplateId.value) return null;
  return reminderTemplates.value.find(t => t.id === selectedTemplateId.value);
});

// Get label for remind_before value
const getRemindBeforeLabel = value => {
  const option = reminderOptions.find(o => o.value === value);
  return option
    ? option.label
    : `${value} ${t('APPOINTMENTS.FORM.MINUTES_BEFORE')}`;
};

// Search contacts via API
const searchContacts = async query => {
  if (!query || query.length < 2) {
    searchResults.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  try {
    const response = await ContactsAPI.search(query, 1, 'name');
    searchResults.value = response.data.payload || [];
  } catch (error) {
    console.error('Error searching contacts:', error);
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

// Debounced search
const debouncedSearch = query => {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchContacts(query);
  }, 300);
};

// Watch for search input changes
watch(searchContact, newValue => {
  if (newValue && newValue.length >= 2) {
    isSearching.value = true;
    debouncedSearch(newValue);
  } else {
    searchResults.value = [];
  }
});

onMounted(async () => {
  // Fetch reminder templates
  await fetchReminderTemplates();

  // Apply account default settings for new appointments
  const applyDefaultSettings = () => {
    const settings = accountSettings.value;

    // Apply default reminder settings from account
    sendReminder.value =
      settings.appointment_reminder_enabled_by_default !== false;
    reminderMinutes.value = settings.appointment_reminder_default_minutes || 30;
    reminderMessage.value =
      settings.appointment_reminder_default_message || defaultReminderTemplate;

    // Use conversation inbox if provided, otherwise use account default inbox
    if (props.conversationInboxId) {
      reminderInboxId.value = props.conversationInboxId;
    } else {
      reminderInboxId.value =
        settings.appointment_reminder_default_inbox_id || null;
    }
  };

  if (props.appointment) {
    // Edit mode
    title.value = props.appointment.title;
    description.value = props.appointment.description || '';
    location.value = props.appointment.location || '';
    color.value = props.appointment.color || '#3b82f6';
    contactId.value = props.appointment.contact_id;
    conversationId.value = props.appointment.conversation_id;
    allDay.value = props.appointment.all_day;

    const start = new Date(props.appointment.start_time);
    const end = new Date(props.appointment.end_time);

    startDate.value = start.toISOString().split('T')[0];
    startTime.value = start.toTimeString().slice(0, 5);
    endDate.value = end.toISOString().split('T')[0];
    endTime.value = end.toTimeString().slice(0, 5);

    // Load reminder settings if exists
    if (props.appointment.reminders?.length > 0) {
      const reminder = props.appointment.reminders[0];
      sendReminder.value = true;
      reminderMinutes.value = reminder.remind_before;
      reminderType.value = reminder.reminder_type;
      reminderMessage.value = reminder.message_template || '';
      reminderInboxId.value = reminder.inbox_id || null;
      // Load template if set
      selectedTemplateId.value = reminder.template_id || null;
    } else {
      // No reminder on this appointment - apply defaults for display
      applyDefaultSettings();
      sendReminder.value = false; // But keep it off since the appointment had no reminder
    }

    // Load contact info if exists
    if (props.appointment.contact_id && props.appointment.contact) {
      selectedContact.value = props.appointment.contact;
    } else if (props.appointment.contact_id) {
      try {
        const response = await ContactsAPI.show(props.appointment.contact_id);
        selectedContact.value = response.data.payload;
      } catch (error) {
        console.error('Error loading contact:', error);
      }
    }
  } else {
    // New appointment - apply default settings
    applyDefaultSettings();

    if (props.initialDate) {
      // New appointment with initial date
      const date = new Date(props.initialDate);
      const now = new Date();

      startDate.value = date.toISOString().split('T')[0];
      endDate.value = date.toISOString().split('T')[0];

      // Check if it's today - if so, use current time rounded to next hour
      const isToday = date.toDateString() === now.toDateString();
      const hour = isToday ? now.getHours() + 1 : 9; // Default to 9:00 for future dates
      const safeHour = hour > 23 ? 23 : hour;

      startTime.value = `${safeHour.toString().padStart(2, '0')}:00`;
      endTime.value = `${(safeHour + 1 > 23 ? 23 : safeHour + 1).toString().padStart(2, '0')}:00`;
    } else {
      // New appointment without date
      const now = new Date();
      startDate.value = now.toISOString().split('T')[0];
      endDate.value = now.toISOString().split('T')[0];

      const hour = now.getHours() + 1;
      const safeHour = hour > 23 ? 23 : hour;
      startTime.value = `${safeHour.toString().padStart(2, '0')}:00`;
      endTime.value = `${(safeHour + 1 > 23 ? 23 : safeHour + 1).toString().padStart(2, '0')}:00`;
    }
  }

  // Load prefilled contact if provided (for new appointments from conversation)
  if (!props.appointment && props.prefilledContactId) {
    contactId.value = props.prefilledContactId;
    conversationId.value = props.prefilledConversationId;
    try {
      const response = await ContactsAPI.show(props.prefilledContactId);
      selectedContact.value = response.data.payload;
    } catch (error) {
      console.error('Error loading prefilled contact:', error);
    }
  }
});

// Auto-update end time when start time changes
watch(startTime, newValue => {
  if (newValue && !isEditing.value) {
    const [hours] = newValue.split(':').map(Number);
    endTime.value = `${(hours + 1).toString().padStart(2, '0')}:00`;
  }
});

watch(startDate, newValue => {
  if (newValue && !endDate.value) {
    endDate.value = newValue;
  }
});

const canSave = computed(() => {
  return title.value.trim() && startDate.value && endDate.value;
});

const handleSave = () => {
  const startDateTime = allDay.value
    ? new Date(`${startDate.value}T00:00:00`)
    : new Date(`${startDate.value}T${startTime.value}:00`);

  const endDateTime = allDay.value
    ? new Date(`${endDate.value}T23:59:59`)
    : new Date(`${endDate.value}T${endTime.value}:00`);

  const appointmentData = {
    title: title.value.trim(),
    description: description.value.trim() || null,
    start_time: startDateTime.toISOString(),
    end_time: endDateTime.toISOString(),
    all_day: allDay.value,
    location: location.value.trim() || null,
    color: color.value,
    contact_id: contactId.value || null,
    conversation_id: conversationId.value || null,
  };

  // Add reminders
  if (sendReminder.value) {
    // Use template data if selected, otherwise use manual values
    if (selectedTemplateId.value && selectedTemplate.value) {
      appointmentData.reminders = [
        {
          minutes_before: selectedTemplate.value.remind_before,
          type: 'message',
          message_template: selectedTemplate.value.message_template,
          inbox_id: selectedTemplate.value.inbox_id || reminderInboxId.value,
          template_id: selectedTemplateId.value,
        },
      ];
    } else {
      appointmentData.reminders = [
        {
          minutes_before: reminderMinutes.value,
          type: reminderType.value,
          message_template:
            reminderType.value !== 'notification'
              ? reminderMessage.value
              : null,
          inbox_id:
            reminderType.value !== 'notification'
              ? reminderInboxId.value
              : null,
        },
      ];
    }
  }

  emit('save', appointmentData);
};

const handleDelete = () => {
  if (props.appointment) {
    emit('delete', props.appointment.id);
  }
};

const handleSendReminderNow = async () => {
  // Determine inbox and message based on template or manual config
  let inboxId = reminderInboxId.value;
  let messageTemplate = reminderMessage.value || defaultReminderTemplate;

  if (selectedTemplateId.value && selectedTemplate.value) {
    inboxId = selectedTemplate.value.inbox_id || reminderInboxId.value;
    messageTemplate = selectedTemplate.value.message_template;
  }

  if (!props.appointment?.id || !contactId.value || !inboxId) {
    useAlert(t('APPOINTMENTS.REMINDER.MISSING_DATA'));
    return;
  }

  isSendingReminder.value = true;
  try {
    await AppointmentsAPI.sendReminder(props.appointment.id, {
      inbox_id: inboxId,
      message_template: messageTemplate,
    });
    useAlert(t('APPOINTMENTS.REMINDER.SENT_SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('APPOINTMENTS.REMINDER.SENT_ERROR');
    useAlert(errorMessage);
  } finally {
    isSendingReminder.value = false;
  }
};

const selectContact = contact => {
  contactId.value = contact.id;
  selectedContact.value = contact;
  searchContact.value = '';
  showContactDropdown.value = false;
  searchResults.value = [];
  // Clear conversation_id when selecting a different contact via search
  // since the conversation may not be related to this contact
  conversationId.value = null;
};

const clearContact = () => {
  contactId.value = null;
  selectedContact.value = null;
  conversationId.value = null;
};

const handleSearchFocus = () => {
  showContactDropdown.value = true;
};

const handleSearchBlur = () => {
  // Delay to allow click on dropdown item
  setTimeout(() => {
    showContactDropdown.value = false;
  }, 200);
};

const getContactDisplayName = contact => {
  if (!contact) return '';
  return contact.name || contact.email || contact.phone_number || 'Contato';
};

const getContactSubtitle = contact => {
  if (!contact) return '';
  const parts = [];
  if (contact.phone_number) parts.push(contact.phone_number);
  if (contact.email && contact.name) parts.push(contact.email);
  return parts.join(' • ');
};
</script>

<template>
  <Modal :show="true" :on-close="() => emit('close')">
    <div class="appointment-modal">
      <div class="modal-header">
        <h2>
          {{ isEditing ? t('APPOINTMENTS.EDIT') : t('APPOINTMENTS.NEW') }}
        </h2>
        <button class="close-btn" @click="emit('close')">
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <div class="modal-body">
        <!-- Title -->
        <div class="form-group">
          <label class="form-label required">{{
            t('APPOINTMENTS.FORM.TITLE')
          }}</label>
          <input
            v-model="title"
            type="text"
            class="form-input"
            :placeholder="t('APPOINTMENTS.FORM.TITLE_PLACEHOLDER')"
          />
        </div>

        <!-- Date & Time -->
        <div class="form-section">
          <div class="form-row">
            <div class="form-group flex-1">
              <label class="form-label">{{
                t('APPOINTMENTS.FORM.START_DATE')
              }}</label>
              <input v-model="startDate" type="date" class="form-input" />
            </div>
            <div v-if="!allDay" class="form-group time-group">
              <label class="form-label">{{
                t('APPOINTMENTS.FORM.TIME')
              }}</label>
              <input v-model="startTime" type="time" class="form-input" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group flex-1">
              <label class="form-label">{{
                t('APPOINTMENTS.FORM.END_DATE')
              }}</label>
              <input v-model="endDate" type="date" class="form-input" />
            </div>
            <div v-if="!allDay" class="form-group time-group">
              <label class="form-label">{{
                t('APPOINTMENTS.FORM.TIME')
              }}</label>
              <input v-model="endTime" type="time" class="form-input" />
            </div>
          </div>

          <label class="checkbox-label">
            <input v-model="allDay" type="checkbox" class="form-checkbox" />
            <span>{{ t('APPOINTMENTS.FORM.ALL_DAY') }}</span>
          </label>
        </div>

        <!-- Location -->
        <div class="form-group">
          <label class="form-label">
            <span class="i-lucide-map-pin size-4" />
            {{ t('APPOINTMENTS.FORM.LOCATION') }}
          </label>
          <input
            v-model="location"
            type="text"
            class="form-input"
            :placeholder="t('APPOINTMENTS.FORM.LOCATION_PLACEHOLDER')"
          />
        </div>

        <!-- Description -->
        <div class="form-group">
          <label class="form-label">{{
            t('APPOINTMENTS.FORM.DESCRIPTION')
          }}</label>
          <textarea
            v-model="description"
            rows="3"
            class="form-textarea"
            :placeholder="t('APPOINTMENTS.FORM.DESCRIPTION_PLACEHOLDER')"
          />
        </div>

        <!-- Contact Search -->
        <div class="form-group">
          <label class="form-label">
            <span class="i-lucide-user size-4" />
            {{ t('APPOINTMENTS.FORM.CONTACT') }}
          </label>

          <div v-if="selectedContact" class="selected-contact">
            <div class="contact-avatar">
              <img
                v-if="selectedContact.thumbnail"
                :src="selectedContact.thumbnail"
                :alt="getContactDisplayName(selectedContact)"
              />
              <span v-else class="avatar-placeholder">
                {{
                  getContactDisplayName(selectedContact).charAt(0).toUpperCase()
                }}
              </span>
            </div>
            <div class="contact-info">
              <span class="contact-name">{{
                getContactDisplayName(selectedContact)
              }}</span>
              <span class="contact-subtitle">{{
                getContactSubtitle(selectedContact)
              }}</span>
            </div>
            <button class="clear-btn" @click="clearContact">
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <div v-else class="contact-search">
            <div class="search-input-wrapper">
              <span class="i-lucide-search size-4 search-icon" />
              <input
                v-model="searchContact"
                type="text"
                class="search-input"
                :placeholder="t('APPOINTMENTS.FORM.CONTACT_PLACEHOLDER')"
                @focus="handleSearchFocus"
                @blur="handleSearchBlur"
              />
              <span
                v-if="isSearching"
                class="i-lucide-loader-2 size-4 animate-spin loading-icon"
              />
            </div>

            <div v-if="showContactDropdown" class="contact-dropdown">
              <div v-if="isSearching" class="dropdown-loading">
                <span class="i-lucide-loader-2 size-5 animate-spin" />
                <span>Buscando...</span>
              </div>
              <div
                v-else-if="
                  searchResults.length === 0 && searchContact.length >= 2
                "
                class="dropdown-empty"
              >
                <span class="i-lucide-user-x size-5" />
                <span>Nenhum contato encontrado</span>
              </div>
              <div v-else-if="searchContact.length < 2" class="dropdown-hint">
                <span>Digite pelo menos 2 caracteres para buscar</span>
              </div>
              <button
                v-for="contact in searchResults"
                :key="contact.id"
                class="contact-option"
                @mousedown="selectContact(contact)"
              >
                <div class="contact-avatar small">
                  <img
                    v-if="contact.thumbnail"
                    :src="contact.thumbnail"
                    :alt="getContactDisplayName(contact)"
                  />
                  <span v-else class="avatar-placeholder">
                    {{ getContactDisplayName(contact).charAt(0).toUpperCase() }}
                  </span>
                </div>
                <div class="contact-info">
                  <span class="contact-name">{{
                    getContactDisplayName(contact)
                  }}</span>
                  <span class="contact-subtitle">{{
                    getContactSubtitle(contact)
                  }}</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Color -->
        <div class="form-group">
          <label class="form-label">{{ t('APPOINTMENTS.FORM.COLOR') }}</label>
          <div class="color-picker">
            <button
              v-for="c in colors"
              :key="c"
              class="color-option"
              :class="{ selected: color === c }"
              :style="{ backgroundColor: c }"
              @click="color = c"
            />
          </div>
        </div>

        <!-- Reminder -->
        <div class="form-section reminder-section">
          <label class="checkbox-label">
            <input
              v-model="sendReminder"
              type="checkbox"
              class="form-checkbox"
            />
            <span class="i-lucide-bell size-4" />
            <span>{{ t('APPOINTMENTS.FORM.SEND_REMINDER') }}</span>
          </label>

          <div v-if="sendReminder" class="reminder-settings">
            <!-- Template selector -->
            <div v-if="reminderTemplates.length > 0" class="form-group">
              <label class="form-label">
                <span class="i-lucide-file-text size-4" />
                {{ t('APPOINTMENTS.FORM.REMINDER_TEMPLATE') }}
              </label>
              <select v-model="selectedTemplateId" class="form-select">
                <option :value="null">
                  {{ t('APPOINTMENTS.FORM.REMINDER_TEMPLATE_MANUAL') }}
                </option>
                <option
                  v-for="template in reminderTemplates"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.name }}
                </option>
              </select>
            </div>

            <!-- Template preview when selected -->
            <div
              v-if="selectedTemplateId && selectedTemplate"
              class="template-preview"
            >
              <div class="preview-item">
                <span class="preview-label">{{ t('APPOINTMENTS.FORM.REMINDER_WHEN') }}:</span>
                <span class="preview-value">{{
                  getRemindBeforeLabel(selectedTemplate.remind_before)
                }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">{{ t('APPOINTMENTS.FORM.REMINDER_INBOX') }}:</span>
                <span class="preview-value">{{
                  selectedTemplate.inbox?.name ||
                  t('APPOINTMENTS.FORM.CONVERSATION_INBOX')
                }}</span>
              </div>
              <div class="preview-item preview-message">
                <span class="preview-label">{{ t('APPOINTMENTS.FORM.REMINDER_MESSAGE') }}:</span>
                <span class="preview-value">{{
                  selectedTemplate.message_template
                }}</span>
              </div>
            </div>

            <!-- Manual configuration (only when no template selected) -->
            <template v-if="!selectedTemplateId">
              <div class="form-row">
                <div class="form-group flex-1">
                  <label class="form-label">{{
                    t('APPOINTMENTS.FORM.REMINDER_WHEN')
                  }}</label>
                  <select v-model="reminderMinutes" class="form-select">
                    <option
                      v-for="opt in reminderOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="form-group flex-1">
                  <label class="form-label">{{
                    t('APPOINTMENTS.FORM.REMINDER_TYPE')
                  }}</label>
                  <select v-model="reminderType" class="form-select">
                    <option value="notification">
                      {{ t('APPOINTMENTS.REMINDER_TYPES.NOTIFICATION') }}
                    </option>
                    <option value="message">
                      {{ t('APPOINTMENTS.REMINDER_TYPES.MESSAGE') }}
                    </option>
                    <option value="both">
                      {{ t('APPOINTMENTS.REMINDER_TYPES.BOTH') }}
                    </option>
                  </select>
                </div>
              </div>

              <div v-if="reminderType !== 'notification'" class="form-group">
                <label class="form-label">
                  <span class="i-lucide-inbox size-4" />
                  {{ t('APPOINTMENTS.FORM.REMINDER_INBOX') }}
                </label>
                <select v-model="reminderInboxId" class="form-select">
                  <option :value="null">
                    {{ t('APPOINTMENTS.FORM.REMINDER_INBOX_PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="inbox in availableInboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
                <p class="help-text">
                  {{ t('APPOINTMENTS.FORM.REMINDER_INBOX_HELP') }}
                </p>
              </div>

              <div v-if="reminderType !== 'notification'" class="form-group">
                <label class="form-label">{{
                  t('APPOINTMENTS.FORM.REMINDER_MESSAGE')
                }}</label>
                <textarea
                  v-model="reminderMessage"
                  rows="3"
                  class="form-textarea"
                  :placeholder="
                    t('APPOINTMENTS.FORM.REMINDER_MESSAGE_PLACEHOLDER')
                  "
                />
                <p class="help-text">
                  {{ t('APPOINTMENTS.FORM.REMINDER_VARIABLES') }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <Button
          v-if="isEditing"
          variant="ghost"
          color="ruby"
          @click="handleDelete"
        >
          {{ t('APPOINTMENTS.ACTIONS.DELETE') }}
        </Button>
        <Button
          v-if="canSendReminder"
          variant="ghost"
          color="jade"
          :is-loading="isSendingReminder"
          @click="handleSendReminderNow"
        >
          <span class="i-lucide-send size-4 mr-1" />
          {{ t('APPOINTMENTS.ACTIONS.SEND_REMINDER') }}
        </Button>
        <div class="flex-1" />
        <Button variant="ghost" @click="emit('close')">
          {{ t('APPOINTMENTS.ACTIONS.CANCEL') }}
        </Button>
        <Button :disabled="!canSave" @click="handleSave">
          {{
            isEditing
              ? t('APPOINTMENTS.ACTIONS.SAVE')
              : t('APPOINTMENTS.ACTIONS.CREATE')
          }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.appointment-modal {
  width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: var(--color-background);
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
  background: var(--s-50);
}

.dark .modal-header {
  background: var(--s-900);
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--s-900);
}

.dark .modal-header h2 {
  color: var(--s-100);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--s-500);
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--s-100);
  color: var(--s-700);
}

.dark .close-btn:hover {
  background: var(--s-800);
  color: var(--s-300);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.form-section {
  margin-bottom: 20px;
  padding: 16px;
  background: var(--s-50);
  border-radius: 12px;
}

.dark .form-section {
  background: var(--s-900);
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--s-700);
  margin-bottom: 8px;
}

.dark .form-label {
  color: var(--s-300);
}

.form-label.required::after {
  content: '*';
  color: var(--r-500);
}

.form-row {
  display: flex;
  gap: 12px;
}

.flex-1 {
  flex: 1;
}

.time-group {
  width: 120px;
  flex-shrink: 0;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid var(--s-200);
  border-radius: 10px;
  background: var(--color-background);
  color: var(--s-900);
  transition: all 0.2s;
}

.dark .form-input,
.dark .form-select,
.dark .form-textarea {
  border-color: var(--s-700);
  background: var(--s-800);
  color: var(--s-100);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #1f93ff;
  box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  accent-color: #1f93ff;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--s-700);
  cursor: pointer;
}

.dark .checkbox-label {
  color: var(--s-300);
}

.color-picker {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.color-option {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.selected {
  border-color: var(--s-900);
  box-shadow: 0 0 0 2px white;
}

.dark .color-option.selected {
  border-color: white;
  box-shadow: 0 0 0 2px var(--s-800);
}

/* Contact Search */
.selected-contact {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--s-50);
  border-radius: 12px;
  border: 1px solid var(--s-200);
}

.dark .selected-contact {
  background: var(--s-800);
  border-color: var(--s-700);
}

.contact-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--s-200);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.contact-avatar.small {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.contact-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  font-size: 16px;
  font-weight: 600;
  color: var(--s-600);
}

.contact-avatar.small .avatar-placeholder {
  font-size: 14px;
}

.contact-info {
  flex: 1;
  min-width: 0;
}

.contact-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--s-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark .contact-name {
  color: var(--s-100);
}

.contact-subtitle {
  display: block;
  font-size: 12px;
  color: var(--s-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--s-500);
  cursor: pointer;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: var(--s-200);
  color: var(--s-700);
}

.dark .clear-btn:hover {
  background: var(--s-700);
  color: var(--s-300);
}

.contact-search {
  position: relative;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid var(--s-200);
  border-radius: 10px;
  background: var(--color-background);
  transition: all 0.2s;
}

.dark .search-input-wrapper {
  border-color: var(--s-700);
  background: var(--s-800);
}

.search-input-wrapper:focus-within {
  border-color: #1f93ff;
  box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.1);
}

.search-icon,
.loading-icon {
  color: var(--s-400);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: var(--s-900);
  outline: none;
}

.dark .search-input {
  color: var(--s-100);
}

.search-input::placeholder {
  color: var(--s-400);
}

.contact-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--color-background);
  border: 1px solid var(--s-200);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 100;
  max-height: 280px;
  overflow-y: auto;
}

.dark .contact-dropdown {
  background: var(--s-800);
  border-color: var(--s-700);
}

.dropdown-loading,
.dropdown-empty,
.dropdown-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: var(--s-500);
  font-size: 13px;
}

.contact-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

.contact-option:hover {
  background: var(--s-50);
}

.dark .contact-option:hover {
  background: var(--s-700);
}

.contact-option:first-child {
  border-radius: 12px 12px 0 0;
}

.contact-option:last-child {
  border-radius: 0 0 12px 12px;
}

/* Reminder Section */
.reminder-section {
  background: var(--s-50);
  border-radius: 12px;
  padding: 16px;
  margin-top: 8px;
}

.dark .reminder-section {
  background: var(--s-900);
}

.reminder-settings {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--s-200);
}

.dark .reminder-settings {
  border-color: var(--s-700);
}

.help-text {
  font-size: 11px;
  color: var(--s-500);
  margin-top: 6px;
  line-height: 1.4;
}

/* Template Preview */
.template-preview {
  margin-top: 12px;
  padding: 12px;
  background: var(--s-100);
  border-radius: 8px;
  border: 1px solid var(--s-200);
}

.dark .template-preview {
  background: var(--s-800);
  border-color: var(--s-700);
}

.preview-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-item.preview-message {
  flex-direction: column;
  gap: 4px;
}

.preview-label {
  font-weight: 500;
  color: var(--s-600);
  white-space: nowrap;
}

.dark .preview-label {
  color: var(--s-400);
}

.preview-value {
  color: var(--s-900);
}

.dark .preview-value {
  color: var(--s-100);
}

.preview-message .preview-value {
  padding: 8px;
  background: var(--color-background);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
}

.modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  background: var(--s-25);
}

.dark .modal-footer {
  background: var(--s-900);
}
</style>
